import {
  PROXY,
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { PRD, REG_1709, VEHICLE_ORGAN, allOf, vehicleFields } from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F1. Wniosek o rejestrację pojazdu — art. 72, 73 i 73aa ustawy Prawo o ruchu drogowym, § 2–4 rozporządzenia MI
 * z 8.11.2024 r. (Dz. U. poz. 1709); opłaty: rozporządzenie MI z 4.08.2022 r. (t.j. Dz. U. 2025 poz. 553).
 * Układ pól według wzoru ogólnopolskiego — załącznik nr 1 do rozporządzenia (wspólny wzór: rejestracja / czasowa
 * rejestracja / wyrejestrowanie / zbycie), tekst https://api.sejm.gov.pl/eli/acts/DU/2024/1709/text.pdf — pobrany
 * 2026-10-22 (pkt 1–6 danych pojazdu, tablice indywidualne i zmniejszone, zachowanie numeru); oświadczenia o tablicach
 * pojazdu sprowadzonego i o utracie tablicy dodatkowej z § 2 ust. 1 pkt 4–5 (mogą być złożone na wniosku). Lista
 * załączników z § 2–3 rozporządzenia i katalogu; dowód odprawy celnej i akcyzy nieobowiązkowe, bo organ celny/KAS może je
 * przekazać elektronicznie (§ 3 ust. 3–6). Czasowa rejestracja i wyrejestrowanie — poza tym wnioskiem.
 */
const NEW: EbiCondition = { field: "pochodzenie", equals: "nowy" };
const REGISTERED_PL: EbiCondition = { field: "pochodzenie", equals: "zarejestrowanyPl" };
const FROM_EU: EbiCondition = { field: "pochodzenie", equals: "sprowadzonyUe" };
const FROM_NON_EU: EbiCondition = { field: "pochodzenie", equals: "sprowadzonySpozaUe" };
const IMPORTED: EbiCondition = { field: "pochodzenie", in: ["sprowadzonyUe", "sprowadzonySpozaUe"] };
const USED: EbiCondition = { field: "pochodzenie", in: ["zarejestrowanyPl", "sprowadzonyUe", "sprowadzonySpozaUe"] };
const CO_OWNERS: EbiCondition = { field: "wspolwlasciciel", equals: "tak" };

const F1: IEbiFormDefinition = {
  code: "F1",
  version: 1,
  title: "Wniosek o rejestrację pojazdu",
  category: EEbiFormCategory.Vehicles,
  office: EEbiFormOffice.County,
  description: "Rejestracja pojazdu nowego, kupionego w Polsce albo sprowadzonego z zagranicy — wydanie dowodu rejestracyjnego i tablic.",
  keywords: ["rejestracja", "pojazd", "samochód", "dowód rejestracyjny", "tablice rejestracyjne", "komunikacja", "sprowadzony"],
  organ: `${VEHICLE_ORGAN}, właściwy ze względu na miejsce zamieszkania albo siedzibę właściciela pojazdu`,
  legalBasis: `art. 72, art. 73 i art. 73aa ${PRD}; § 2 ${REG_1709}`,
  request: "Wnoszę o rejestrację pojazdu opisanego we wniosku.",
  feeLabel: "wg cennika",
  fee: "Dowód rejestracyjny 54 zł, pozwolenie czasowe 13,50 zł, komplet nalepek legalizacyjnych 12,50 zł; tablice: samochodowe 80 zł, motocyklowe 40 zł, motorowerowe 30 zł, indywidualne 1000 zł, zabytkowe 100 zł; pełnomocnictwo 17 zł.",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "wlasciciel",
      title: "Właściciel pojazdu",
      description: "Właścicielem jest wnioskodawca, a gdy wniosek składa pełnomocnik — mocodawca.",
      fields: [
        {
          code: "wlascicielIdentyfikator",
          type: "text",
          label: "Numer PESEL albo REGON mocodawcy (właściciela pojazdu)",
          required: true,
          visibleWhen: PROXY,
          width: "half",
          maxLength: 14,
          hint: "REGON podaje właściciel, któremu go nadano, z wyłączeniem osób fizycznych prowadzących działalność gospodarczą.",
        },
        { code: "wspolwlasciciel", type: "radio", label: "Czy pojazd ma współwłaściciela (współwłaścicieli)?", required: true, defaultValue: "nie", options: YES_NO },
        {
          code: "wspolwlasciciele",
          type: "repeat",
          label: "Współwłaściciele pojazdu",
          itemLabel: "Współwłaściciel",
          required: true,
          min: 1,
          max: 5,
          visibleWhen: CO_OWNERS,
          fields: [
            { code: "nazwa", type: "text", label: "Imię i nazwisko lub nazwa", required: true, width: "half", maxLength: 300 },
            { code: "identyfikator", type: "text", label: "Numer PESEL albo REGON", required: true, width: "half", maxLength: 14 },
            { code: "adres", type: "text", label: "Adres — ulica, numer, kod pocztowy, miejscowość", required: true, maxLength: 300 },
          ],
        },
        feeInfo(
          "infoWspolwlasciciele",
          "Jeżeli składasz wniosek także w imieniu współwłaścicieli, dołącz ich pełnomocnictwa (opłata skarbowa 17 zł od każdego, chyba że pełnomocnictwa udzielił małżonek, wstępny, zstępny lub rodzeństwo).",
        ),
      ],
    },
    {
      code: "pojazd",
      title: "Dane pojazdu",
      fields: [
        feeInfo(
          "infoTermin",
          "Wniosek o rejestrację składa się w terminie 30 dni od dnia nabycia pojazdu albo jego sprowadzenia z zagranicy (art. 73aa Prawa o ruchu drogowym; przedsiębiorca prowadzący działalność w zakresie obrotu pojazdami — 90 dni). Za uchybienie terminowi grozi kara pieniężna (art. 140mb). Przed odbiorem dowodu rejestracyjnego dostarcz do urzędu oryginały dokumentów dołączonych jako skany oraz dotychczasowe tablice rejestracyjne.",
        ),
        {
          code: "pochodzenie",
          type: "radio",
          label: "Rejestrowany pojazd",
          required: true,
          options: [
            { value: "nowy", label: "pojazd nowy, rejestrowany po raz pierwszy" },
            { value: "zarejestrowanyPl", label: "pojazd dotychczas zarejestrowany w Polsce" },
            { value: "sprowadzonyUe", label: "pojazd sprowadzony z państwa członkowskiego Unii Europejskiej" },
            { value: "sprowadzonySpozaUe", label: "pojazd sprowadzony spoza Unii Europejskiej" },
          ],
        },
        ...vehicleFields({ full: true, registrationLabel: "Dotychczasowy numer rejestracyjny", registrationVisibleWhen: USED }),
        {
          code: "dataSprowadzenia",
          type: "date",
          label: "Data sprowadzenia pojazdu z terytorium państwa członkowskiego UE",
          visibleWhen: FROM_EU,
          width: "half",
          hint: "Wypełnia się dla pojazdu rejestrowanego w Polsce po raz pierwszy, sprowadzonego po 31 grudnia 2019 r.",
        },
        {
          code: "samochodOsobowy",
          type: "radio",
          label: "Czy pojazd jest samochodem osobowym?",
          required: true,
          visibleWhen: FROM_EU,
          options: YES_NO,
        },
        {
          code: "badanieAktualne",
          type: "radio",
          label:
            "Czy aktualne badanie techniczne pojazdu jest odnotowane w centralnej ewidencji pojazdów albo w zagranicznym dowodzie rejestracyjnym?",
          required: true,
          visibleWhen: USED,
          options: YES_NO,
        },
        {
          code: "dokumentyObce",
          type: "checkbox",
          label: "Część dokumentów pojazdu jest sporządzona w języku obcym",
          visibleWhen: IMPORTED,
        },
      ],
    },
    {
      code: "tablice",
      title: "Tablice rejestracyjne",
      fields: [
        {
          code: "zachowanieNumeru",
          type: "radio",
          label: "Wnoszę o zachowanie dotychczasowego numeru rejestracyjnego",
          required: true,
          defaultValue: "nie",
          visibleWhen: REGISTERED_PL,
          options: YES_NO,
          hint: "Art. 73 ust. 1a Prawa o ruchu drogowym — także numeru z innego powiatu; urząd zalegalizuje wtedy dotychczasowe tablice.",
        },
        {
          code: "dodatkowaTablica",
          type: "radio",
          label: "Dodatkowa tablica rejestracyjna do oznaczenia bagażnika",
          required: true,
          defaultValue: "nie",
          visibleWhen: REGISTERED_PL,
          options: [
            { value: "nie", label: "nie została wydana" },
            { value: "przekaze", label: "została wydana — przekażę ją w urzędzie" },
            { value: "utracona", label: "została wydana, ale ją utraciłem (utraciłam) — oświadczam o jej utracie" },
          ],
        },
        {
          code: "tabliceZagraniczne",
          type: "radio",
          label: "Tablice rejestracyjne pojazdu sprowadzonego — oświadczam, że",
          required: true,
          visibleWhen: IMPORTED,
          options: [
            { value: "przekaze", label: "przekażę dotychczasowe tablice w urzędzie" },
            { value: "bezTablic", label: "pojazd został sprowadzony z zagranicy bez tablic rejestracyjnych" },
            { value: "zwrot", label: "tablice należy zwrócić (zostały zwrócone) organowi rejestrującemu państwa, z którego pojazd sprowadzono" },
          ],
        },
        { code: "tabliceIndywidualne", type: "checkbox", label: "Wnoszę o wydanie tablic rejestracyjnych indywidualnych" },
        {
          code: "wyroznik",
          type: "text",
          label: "Wyróżnik pojazdu na tablicach indywidualnych",
          required: true,
          visibleWhen: { field: "tabliceIndywidualne", equals: true },
          width: "half",
          maxLength: 10,
        },
        {
          code: "tabliceZmniejszone",
          type: "checkbox",
          label:
            "Wnoszę o wydanie tablic samochodowych zmniejszonych i oświadczam, że w pojeździe są zmniejszone wymiary miejsca konstrukcyjnie przeznaczonego do umieszczenia tablic rejestracyjnych",
        },
        feeInfo(
          "infoOplata",
          "Opłaty: dowód rejestracyjny 54 zł, pozwolenie czasowe 13,50 zł, komplet nalepek legalizacyjnych 12,50 zł, tablice samochodowe 80 zł (motocyklowe 40 zł, motorowerowe 30 zł, indywidualne 1000 zł, zabytkowe 100 zł). Dołącz potwierdzenie przelewu na rachunek urzędu.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "dowodWlasnosci",
      label: "Dowód własności pojazdu",
      hint: "Umowa sprzedaży, zamiany lub darowizny, faktura, orzeczenie sądu, postanowienie o nabyciu spadku. Gdy dane zbywcy nie zgadzają się z właścicielem w dowodzie rejestracyjnym — wszystkie dokumenty potwierdzające przeniesienie własności (§ 4 rozporządzenia).",
      multiple: true,
      required: true,
    },
    {
      code: "dowodRejestracyjny",
      label: "Dotychczasowy dowód rejestracyjny",
      hint: "Skan (przy dowodzie zagranicznym — obie części); oryginał oddajesz w urzędzie. Jeśli dowód zatrzymano — pokwitowanie albo zaświadczenie.",
      visibleWhen: USED,
      required: true,
    },
    {
      code: "badanieTechniczne",
      label: "Zaświadczenie o pozytywnym wyniku badania technicznego pojazdu",
      visibleWhen: allOf(USED, { field: "badanieAktualne", equals: "nie" }),
      required: true,
    },
    {
      code: "swiadectwoZgodnosci",
      label: "Świadectwo zgodności WE albo dopuszczenie jednostkowe pojazdu",
      hint: "Dokument z art. 72 ust. 1 pkt 3 Prawa o ruchu drogowym, z oświadczeniem o dacie pierwszej sprzedaży.",
      visibleWhen: NEW,
      required: true,
    },
    {
      code: "odprawaCelna",
      label: "Dowód odprawy celnej przywozowej",
      hint: "Dołącz, jeśli organ celny nie przekazał go urzędowi elektronicznie (§ 3 ust. 3 i 5 rozporządzenia).",
      visibleWhen: FROM_NON_EU,
    },
    {
      code: "akcyza",
      label: "Dokument potwierdzający zapłatę akcyzy na terytorium kraju albo brak obowiązku jej zapłaty",
      hint: "Dołącz, jeśli organ właściwy w sprawach akcyzy nie przekazał go urzędowi elektronicznie (§ 3 ust. 4 i 6 rozporządzenia).",
      visibleWhen: allOf(FROM_EU, { field: "samochodOsobowy", equals: "tak" }),
    },
    {
      code: "tlumaczenia",
      label: "Tłumaczenia dokumentów sporządzonych w języku obcym",
      hint: "Wykonane przez tłumacza przysięgłego albo konsula.",
      multiple: true,
      visibleWhen: allOf(IMPORTED, { field: "dokumentyObce", equals: true }),
      required: true,
    },
    {
      code: "kartaPojazdu",
      label: "Karta pojazdu — jeśli została wydana",
      hint: "Kart pojazdu nie wydaje się od 4 września 2022 r.",
      visibleWhen: REGISTERED_PL,
    },
    {
      code: "pelnomocnictwaWspolwlascicieli",
      label: "Pełnomocnictwa współwłaścicieli",
      hint: "Gdy składasz wniosek także w imieniu współwłaścicieli.",
      multiple: true,
      visibleWhen: CO_OWNERS,
    },
    ...proxyAttachments(),
    feeProofAttachment("Dowód uiszczenia opłat za dowód rejestracyjny, pozwolenie czasowe, nalepki i tablice rejestracyjne"),
    {
      code: "polisaOc",
      label: "Polisa obowiązkowego ubezpieczenia OC",
      hint: "(praktyka urzędów) — do wglądu; nieobowiązkowo.",
    },
    {
      code: "oswiadczenieMalzonka",
      label: "Oświadczenie małżonka o wspólności majątkowej",
      hint: "(praktyka urzędów) — gdy pojazd nabyto do majątku wspólnego; nieobowiązkowo.",
    },
    otherAttachments(),
  ],
};

export default F1;
